import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';

// Components
import Welcome from './components/Welcome';
import Session from './components/Session';
import Login from './components/Login';
import Loading from './components/Loading';
import Header from './components/Header';
import SpotifyCallback from './components/SpotifyCallback';
import TVMode from './components/TVMode';
import PrivacyPolicy from './components/PrivacyPolicy';
import Support from './components/Support';
import ErrorBoundary from './components/ErrorBoundary';
import PaymentCallback from './components/PaymentCallback';

// Protected Route Component - allows both hosts (authenticated) and guests (with name)
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <Loading />;
  }
  
  // Allow if user is authenticated (host) OR if guest name is provided
  const guestName = sessionStorage.getItem('guestName');
  const hasAccess = user || (guestName && guestName.trim().length >= 2);
  
  if (hasAccess) {
    return children;
  }
  
  // If no access, redirect to home page (not login) - they can enter name there
  return <Navigate to="/" replace />;
};

// Main App Component
const AppContent = () => {
  // The TV display is a fullscreen stage — no site chrome
  const isTV = window.location.pathname.startsWith('/tv/');
  return (
    <div className="min-h-screen bg-gray-900">
      {!isTV && <Header />}
      <Routes>
        <Route path="/" element={<Welcome />} />
        <Route path="/login" element={<Login />} />
        <Route
          path="/payment/callback"
          element={
            <ProtectedRoute>
              <PaymentCallback />
            </ProtectedRoute>
          }
        />
        <Route
          path="/session/:sessionId"
          element={
            <ProtectedRoute>
              <Session />
            </ProtectedRoute>
          }
        />
        <Route path="/spotify/callback" element={<SpotifyCallback />} />
        <Route path="/tv/:sessionId" element={<TVMode />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/support" element={<Support />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

// App with Providers
const App = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SocketProvider>
          <AppContent />
        </SocketProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App; 